import { Color, Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import CryptoCurrencyAAVE from 'images/dashboard/crypto-currencies/aave.svg';
import CryptoCurrencyADA from 'images/dashboard/crypto-currencies/ada.svg';
import CryptoCurrencyALGO from 'images/dashboard/crypto-currencies/algo.svg';
import CryptoCurrencyATOM from 'images/dashboard/crypto-currencies/atom.svg';
import CryptoCurrencyBCH from 'images/dashboard/crypto-currencies/bch.svg';
import CryptoCurrencyBNB from 'images/dashboard/crypto-currencies/bnb.svg';
import CryptoCurrencyBSV from 'images/dashboard/crypto-currencies/bsv.svg';
// PROTIP: Run `document.querySelector(".cmc-table").querySelectorAll(".coin-item-symbol")
// .forEach(function(node) { console.log(node.textContent) })` on https://coinmarketcap.com
// Removed currencies that were in top 50 of coinmarketcap but haven't been added in
// crypto-currencies package. Looks like there is a plan to add them in next batch.
import CryptoCurrencyBTC from 'images/dashboard/crypto-currencies/btc.svg';
import CryptoCurrencyBTG from 'images/dashboard/crypto-currencies/btg.svg';
import CryptoCurrencyDAI from 'images/dashboard/crypto-currencies/dai.svg';
// Not in top 50 coinmarketcap but show up in OFAC list
import CryptoCurrencyDASH from 'images/dashboard/crypto-currencies/dash.svg';
import CryptoCurrencyDOGE from 'images/dashboard/crypto-currencies/doge.svg';
import CryptoCurrencyDOT from 'images/dashboard/crypto-currencies/dot.svg';
import CryptoCurrencyEOS from 'images/dashboard/crypto-currencies/eos.svg';
import CryptoCurrencyETC from 'images/dashboard/crypto-currencies/etc.svg';
import CryptoCurrencyETH from 'images/dashboard/crypto-currencies/eth.svg';
import CryptoCurrencyFIL from 'images/dashboard/crypto-currencies/fil.svg';
// Generic SVG for default fallback
import CryptoCurrencyGeneric from 'images/dashboard/crypto-currencies/generic.svg';
import CryptoCurrencyGRT from 'images/dashboard/crypto-currencies/grt.svg';
import CryptoCurrencyICP from 'images/dashboard/crypto-currencies/icp.svg';
import CryptoCurrencyKSM from 'images/dashboard/crypto-currencies/ksm.svg';
import CryptoCurrencyLINK from 'images/dashboard/crypto-currencies/link.svg';
import CryptoCurrencyLTC from 'images/dashboard/crypto-currencies/ltc.svg';
import CryptoCurrencyMANA from 'images/dashboard/crypto-currencies/mana.svg';
import CryptoCurrencyMATIC from 'images/dashboard/crypto-currencies/matic.svg';
import CryptoCurrencyMIOTA from 'images/dashboard/crypto-currencies/miota.svg';
import CryptoCurrencySOL from 'images/dashboard/crypto-currencies/sol.svg';
import CryptoCurrencyTHETA from 'images/dashboard/crypto-currencies/theta.svg';
import CryptoCurrencyTRX from 'images/dashboard/crypto-currencies/trx.svg';
import CryptoCurrencyUNI from 'images/dashboard/crypto-currencies/uni.svg';
import CryptoCurrencyUSDC from 'images/dashboard/crypto-currencies/usdc.svg';
import CryptoCurrencyUSDT from 'images/dashboard/crypto-currencies/usdt.svg';
import CryptoCurrencyVET from 'images/dashboard/crypto-currencies/vet.svg';
import CryptoCurrencyWBTC from 'images/dashboard/crypto-currencies/wbtc.svg';
import CryptoCurrencyXLM from 'images/dashboard/crypto-currencies/xlm.svg';
import CryptoCurrencyXMR from 'images/dashboard/crypto-currencies/xmr.svg';
import CryptoCurrencyXRP from 'images/dashboard/crypto-currencies/xrp.svg';
import CryptoCurrencyXTZ from 'images/dashboard/crypto-currencies/xtz.svg';
import CryptoCurrencyXVG from 'images/dashboard/crypto-currencies/xvg.svg';
import CryptoCurrencyZEC from 'images/dashboard/crypto-currencies/zec.svg';

const CryptoCurrencySVGContainer = styled.span<{
  height?: string;
  width?: string;
  color?: string;
}>`
  > svg {
    width: ${({ width }) => (width ? width : `${Spacing[5]}`)};
    height: ${({ height }) => (height ? height : `${Spacing[5]}`)};
    fill: ${({ color }) => (color ? color : Color.CoreSlate1000)};
    overflow: visible; // otherwise currency SVGs get clipped
  }
`;

interface CryptoCurrencyIconProps {
  className?: string;
  cryptoCurrencyCode?: string;
  height?: string;
  width?: string;
  color?: string;
}

export const CryptoCurrencyIcon = ({
  className,
  cryptoCurrencyCode,
  height,
  width,
  color,
}: CryptoCurrencyIconProps) => {
  if (cryptoCurrencyCode == null) {
    return <></>;
  }

  return (
    <CryptoCurrencySVGContainer className={className} height={height} width={width} color={color}>
      {cryptoCurrencyIcon(cryptoCurrencyCode)}
    </CryptoCurrencySVGContainer>
  );
};

function cryptoCurrencyIcon(cryptoCurrencyCode: string) {
  switch (cryptoCurrencyCode) {
    case 'BTC':
    case 'XBT': // XBT is ISO code for BTC
      return <CryptoCurrencyBTC />;
    case 'ETH':
      return <CryptoCurrencyETH />;
    case 'BNB':
      return <CryptoCurrencyBNB />;
    case 'USDT':
      return <CryptoCurrencyUSDT />;
    case 'ADA':
      return <CryptoCurrencyADA />;
    case 'SOL':
      return <CryptoCurrencySOL />;
    case 'XRP':
      return <CryptoCurrencyXRP />;
    case 'DOT':
      return <CryptoCurrencyDOT />;
    case 'DOGE':
      return <CryptoCurrencyDOGE />;
    case 'USDC':
      return <CryptoCurrencyUSDC />;
    case 'UNI':
      return <CryptoCurrencyUNI />;
    case 'LINK':
      return <CryptoCurrencyLINK />;
    case 'WBTC':
      return <CryptoCurrencyWBTC />;
    case 'LTC':
      return <CryptoCurrencyLTC />;
    case 'MATIC':
      return <CryptoCurrencyMATIC />;
    case 'ALGO':
      return <CryptoCurrencyALGO />;
    case 'BCH':
      return <CryptoCurrencyBCH />;
    case 'XLM':
      return <CryptoCurrencyXLM />;
    case 'VET':
      return <CryptoCurrencyVET />;
    case 'ATOM':
      return <CryptoCurrencyATOM />;
    case 'ICP':
      return <CryptoCurrencyICP />;
    case 'FIL':
      return <CryptoCurrencyFIL />;
    case 'THETA':
      return <CryptoCurrencyTHETA />;
    case 'TRX':
      return <CryptoCurrencyTRX />;
    case 'ETC':
      return <CryptoCurrencyETC />;
    case 'DAI':
      return <CryptoCurrencyDAI />;
    case 'MANA':
      return <CryptoCurrencyMANA />;
    case 'XTZ':
      return <CryptoCurrencyXTZ />;
    case 'XMR':
      return <CryptoCurrencyXMR />;
    case 'GRT':
      return <CryptoCurrencyGRT />;
    case 'EOS':
      return <CryptoCurrencyEOS />;
    case 'AAVE':
      return <CryptoCurrencyAAVE />;
    case 'MIOTA':
      return <CryptoCurrencyMIOTA />;
    case 'KSM':
      return <CryptoCurrencyKSM />;
    case 'DASH':
      return <CryptoCurrencyDASH />;
    case 'XVG':
      return <CryptoCurrencyXVG />;
    case 'BTG':
      return <CryptoCurrencyBTG />;
    case 'ZEC':
      return <CryptoCurrencyZEC />;
    case 'BSV':
      return <CryptoCurrencyBSV />;
    default:
      return <CryptoCurrencyGeneric />;
  }
}
